import { DecisionGraph } from "@imminently/decisively-core";
import {type GraphNode, Relationship} from "@packages/commons";
import { pull } from "lodash";

const log = (...messages: any[]) => {
  if (process.env.NODE_ENV === "test") {
    return;
  }
  console.warn(...messages);
};

const collectDocIds = (docs: any, seen = new WeakSet()) => {
  const result: string[] = [];
  // return undefined if we get undefined
  if (!docs) return docs;
  for (const doc of Object.values(docs) as any) {
    if (!doc || typeof doc !== "object") continue;
    if (seen.has(doc)) continue;
    seen.add(doc);

    if (doc.reference) result.push(doc.reference);
    else result.push(...collectDocIds(doc, seen));
  }
  return result;
};

/** Strip the conclusion data of the node's parents (they reference it) and remove it (with its edges). */
const pruneNode = (rule_graph: DecisionGraph, id: number, description: string | undefined, removeNode: (id: number) => void) => {
  for (const p of [...rule_graph.predecessors(id)]) {
    const p_data = rule_graph.node(p);
    if (p_data) {
      p_data.conditions = undefined;
      p_data.rows = undefined;
      p_data.value = undefined;
      p_data.otherwise = undefined;
    }
  }

  log("cleanGraph", `Stripping old node '${description}'`, undefined, undefined, {
    showInTests: false,
  });
  removeNode(id);
};

export const cleanGraph = (
  rule_graph: DecisionGraph,
  sub_graph: DecisionGraph,
  documentId: string,
  documents: any,
  relationships: Relationship[]
): GraphNode[] => {
  const removed: GraphNode[] = [];
  const used_nodes = new Set(((sub_graph.label() as any)?.usedAttributes || []) as string[]);

  const allDocIds = collectDocIds(documents);

  const allNodes = rule_graph.ids();
  // nodes without a description, for the duplicate check of an identifier without one (the graph's description index
  // only holds descriptions); made when first needed
  let undescribed: Set<number> | undefined;
  const nodesWithDescription = (description: string | undefined): number[] => {
    if (typeof description === "string") return rule_graph.nodesByDescription(description);
    if (!undescribed) {
      undescribed = new Set(allNodes.filter((id) => rule_graph.isListed(id) && rule_graph.field(id, "description") === undefined));
    }
    return [...undescribed];
  };
  const removeNode = (id: number) => {
    undescribed?.delete(id);
    rule_graph.removeNode(id);
  };
  /** A copy of the node, for the removed list (taken before it's removed). */
  const copy = (id: number): GraphNode => ({ ...rule_graph.node(id) }) as GraphNode;

  for (const n of allNodes) {
    // we can remove nodes along the way so may not exist anymore
    if (!rule_graph.isListed(n)) continue;
    // fields are read without making the node object (a copy of its data); writes go through the node object
    const get = (name: string): any => rule_graph.field(n, name);
    const node = () => rule_graph.node(n) as GraphNode;
    const key = rule_graph.deref(n);

    if (!get("entity")) {
      node().entity = "global";
    }
    if (get("_xgen_id")) (node() as any)._xgen_id = undefined;
    // A bug has occurred where the hidden value is sometimes set to true for entities. This shouldn't happen, but this will clean it up
    const hidden = get("entity") !== "global";
    if (get("hidden") !== hidden) node().hidden = hidden;

    if (typeof get("definedIn") === "undefined") {
      // It's not defined anywhere - which should mean it does not have any edges
      // For some reason it has children
      for (const c of [...rule_graph.successors(n)]) {
        rule_graph.removeEdge(n, c);
      }

      // if there are other nodes with the same description, remove this one
      const description = get("description");
      if (description) {
        const duplicate = rule_graph.nodesByDescription(description, { normalized: true }).some((other) => other !== n);
        if (duplicate) {
          removed.push(copy(n));
          pruneNode(rule_graph, n, description, removeNode);
          continue;
        }
      }

      if (get("conditions") || get("rows") || get("value") || get("otherwise") || get("inferred")) {
        const data = node();
        data.conditions = undefined;
        data.rows = undefined;
        data.value = undefined;
        data.otherwise = undefined;
        data.inferred = undefined;
      }
    }

    if (get("identifier") || get("fk")) {
      /*if (data.hidden && data.identifier) {
        //console.log(entity_map)
        // identifiers can't have values
        data.value = undefined;
        data.condition = undefined;
        data.otherwise = undefined;
        data.rows = undefined;
        data.definedIn = undefined;
      }*/

      let noLongerIdentifier = false;

      if (get("identifier") && relationships) {
        const description = get("description");
        const relationship = relationships.find(other => other.textSingular === description);
        if (!relationship) {
          if (!get("usedIn")?.length) {
            removeNode(n);
            continue;
          }

          node().identifier = undefined;
          noLongerIdentifier = true;
        }
      }

      if (!noLongerIdentifier) {
        const description = get("description");
        for (const duplicate of nodesWithDescription(description)) {
          if (duplicate !== n && rule_graph.isListed(duplicate)) {
            removed.push(copy(duplicate));
            removeNode(duplicate);
            log("cleanGraph", `Stripping duplicate node '${description}'`, {node: rule_graph.node(n)}, undefined, {
              showInTest: false,
            });
          }
        }

        const usedIn = get("usedIn");
        if (usedIn) {
          if (get("definedIn") !== documentId && usedIn.indexOf(documentId) > -1) {
            // The attribute was recorded as being used in this document
            if (used_nodes.has(key)) {
              // It was used, and still is. Shouldn't need to do anything
              // The used_nodes contains the list of nodes in the sub_graph, eg: the nodes that are used by rules in the xgen (even though they may be in other documents)
            } else {
              // It's no longer used in this document (likely due to the user deleting the node from the document)
              pull(node().usedIn!, documentId);
            }
          }
        }

        continue; // Ignore - never remove identifiers
      }
    }

    if (get("definedIn") === documentId) {
      const subId = sub_graph.ref(key);
      const sub_node = subId === undefined ? undefined : sub_graph.node(subId);
      //console.log('sub_node', sub_node)
      if (!sub_node || (sub_node && typeof sub_node.definedIn === "undefined")) {
        // THe attribute was once defined in this document, but now it's no longer defined here (maybe they changed a conclusion in a sheet into a condition)
        // Strip any conclusion data from this
        log(
          "cleanGraph",
          `Stripping old node as no longer found in sub graph (or changed from conclusion to condition)'${get("description")}'`,
          { node: rule_graph.node(n), sub_node },
          undefined,
          {
            showInTest: true,
          },
        );
        const data = node();
        data.conditions = undefined;
        data.rows = undefined;
        data.value = undefined;
        data.otherwise = undefined;
        data.definedIn = undefined;
        (data as any).reference = undefined;
        for (const c of [...rule_graph.successors(n)]) {
          rule_graph.removeEdge(n, c);
        }
      }
    }

    if (get("usedIn") instanceof Set) {
      node().usedIn = Array.from(get("usedIn"));
    }

    if (get("usedIn")) {
      // First clean this of any deleted documents
      if (documents !== undefined && get("usedIn").some((d: string) => allDocIds.indexOf(d) === -1)) {
        const usedIn = node().usedIn!;
        for (const d of usedIn) {
          if (allDocIds.indexOf(d) === -1) pull(usedIn, d);
        }
      }
      const usedIn: string[] = get("usedIn");
      if (usedIn.indexOf(documentId) > -1) {
        if (get("definedIn") === documentId) {
          continue;
        }
        // The attribute was recorded as being used in this document
        if (used_nodes.has(key)) {
          // It was used, and still is. Shouldn't need to do anything
          // The used_nodes contains the list of nodes in the sub_graph, eg: the nodes that are used by rules in the xgen (even though they may be in other documents)
        } else {
          // It's no longer used in this document (likely due to the user deleting the node from the document)
          const usedIn = node().usedIn!;
          pull(usedIn, documentId);
          if (usedIn.length === 0) {
            // Not used in any other documents any more - remove the node
            log(
              "cleanGraph",
              "Stripping node as was used in this document, but no longer is and not used anywhere else",
            );
            removed.push(copy(n));
            pruneNode(rule_graph, n, get("description"), removeNode);
            continue;
          }
        }
      } else if (usedIn.length === 0 && !get("definedIn")) {
        // It's not defined or used anywhere
        log("cleanGraph", `Stripping old node as no longer defined or used anywhere '${get("description")}'`, {
          node: rule_graph.node(n),
        });
        removed.push(copy(n));
        pruneNode(rule_graph, n, get("description"), removeNode);
        continue;
      }
    }
    // Final orphan check
    const definedIn = get("definedIn");
    if (documents !== undefined && definedIn && allDocIds.indexOf(definedIn) === -1) {
      // It's not defined in any document, but it could be an input node
      node().definedIn = undefined;
      if (!get("usedIn")?.length) {
        // It's not used anywhere either
        log("cleanGraph", "Stripping node as belongs to a deleted document and is no longer used anywhere", rule_graph.node(n));
        removed.push(copy(n));
        pruneNode(rule_graph, n, get("description"), removeNode);
        continue;
      }
    }
  }

  return removed;
};
